import { useMemo, useState } from 'react';

import { normalizeOrderLine, type OrderLine, type OrderLineRow } from './order-lines-card-columns';

type UseCardOrderLinesProps = {
  lines: OrderLine[];
  initialVisibleLineCount?: number;
};

function buildVisibleLinesWithComponents(lines: OrderLine[]) {
  const visibleLines: OrderLineRow[] = [];
  const componentsByParentId = new Map<string, OrderLineRow[]>();

  // Sort once up front (on a copy, never the input prop) so both the top-level
  // lines and each parent's components come out ordered by line number.
  const sortedLines = [...lines].sort((a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0));

  for (const line of sortedLines) {
    const row = normalizeOrderLine(line);
    if (row.assemblyOrderLineId) {
      const components = componentsByParentId.get(row.assemblyOrderLineId) ?? [];
      components.push(row);
      componentsByParentId.set(row.assemblyOrderLineId, components);
      continue;
    }

    visibleLines.push(row);
  }

  return visibleLines.map((line) => {
    const components = componentsByParentId.get(line.id);
    return components?.length ? { ...line, components } : line;
  });
}

export function useCardOrderLines({ lines, initialVisibleLineCount }: UseCardOrderLinesProps) {
  const [showAllLines, setShowAllLines] = useState(false);
  const visibleLines = useMemo(() => buildVisibleLinesWithComponents(lines), [lines]);
  const isLimitingLines =
    initialVisibleLineCount !== undefined &&
    visibleLines.length > initialVisibleLineCount + 1 &&
    !showAllLines;
  const renderedLines = useMemo(
    () => (isLimitingLines ? visibleLines.slice(0, initialVisibleLineCount) : visibleLines),
    [initialVisibleLineCount, isLimitingLines, visibleLines],
  );

  return {
    visibleLines,
    renderedLines,
    hiddenLineCount: visibleLines.length - renderedLines.length,
    isLimitingLines,
    showAllLines: () => setShowAllLines(true),
  };
}
